/**
 * LanguageSelect Component
 * Custom select with flag icons for language selection
 */
import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import Select, { components, SingleValue, StylesConfig } from 'react-select';

export interface LanguageOption {
  value: string;
  label: string;
  icon: React.ReactNode;
}

interface LanguageSelectProps {
  options: LanguageOption[];
  value: LanguageOption | null;
  onChange: (option: SingleValue<LanguageOption>) => void;
  placeholder?: string;
}

const LanguageLabel: React.FC<{ option: LanguageOption }> = ({ option }) => (
  <Flex alignItems="center" gap="8px" height="100%" minHeight="20px">
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      lineHeight={0}
      flexShrink={0}
    >
      {option.icon}
    </Box>
    <Text
      as="span"
      margin={0}
      lineHeight="1"
      fontSize="14px"
      whiteSpace="nowrap"
    >
      {option.label}
    </Text>
  </Flex>
);

const CustomOption = (props: any) => {
  return (
    <components.Option {...props}>
      <LanguageLabel option={props.data} />
    </components.Option>
  );
};

const CustomSingleValue = (props: any) => {
  return (
    <components.SingleValue {...props}>
      <LanguageLabel option={props.data} />
    </components.SingleValue>
  );
};

const customStyles: StylesConfig<LanguageOption, false> = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '36px',
    height: '36px',
    borderRadius: '8px',
    borderColor: state.isFocused ? '#3182ce' : '#e2e8f0',
    boxShadow: state.isFocused ? '0 0 0 1px #3182ce' : 'none',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      borderColor: '#3182ce',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: '100%',
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    position: 'absolute',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: '100%',
    alignSelf: 'stretch',
    display: 'flex',
    alignItems: 'center',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected
      ? '#ebf8ff'
      : state.isFocused
      ? '#f7fafc'
      : 'white',
    color: state.isSelected ? '#2c5282' : '#2d3748',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    minHeight: '36px',
    '&:active': {
      backgroundColor: '#ebf8ff',
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#2d3748',
    display: 'flex',
    alignItems: 'center',
    margin: 0,
    position: 'static',
    top: 'auto',
    transform: 'none',
    maxWidth: '100%',
    height: '100%',
  }),
};

const LanguageSelect: React.FC<LanguageSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select language',
}) => {
  return (
    <Select<LanguageOption>
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isSearchable={false}
      isClearable={false}
      components={{
        Option: CustomOption,
        SingleValue: CustomSingleValue,
      }}
      styles={customStyles}
      className="language-select"
      classNamePrefix="language-select"
    />
  );
};

export default LanguageSelect;
